// Lint: imports reordered to satisfy import/order.
import React, { FC, useEffect, useMemo, useState } from 'react';

import {
  CrudLevelPermissions,
  NativePermissions,
  PermissionSource,
} from '@ariksa/inventory-core/api';
import { Box, Center, HStack, Stack, Text } from '@chakra-ui/react';

import { StackedCell, WithResourceIcon } from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import {
  permissionEntries,
  PermissionEntry,
} from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Summary/Data/Identity/permissionUtils';

// A Read bucket holds thousands of permissions. Listing them all is what made
// the drawer stutter, so each resource type shows this many until asked.
const ROWS_PER_TYPE = 6;

interface PermissionListProps {
  // one crud level, grouped by native resource type, already filtered by the API
  level?: CrudLevelPermissions;
  search: string;
  // `level` still holds the previous response while the next one is in flight
  isLoading?: boolean;
  supportsUsage: boolean;
  supportsPolicyType: boolean;
}

/**
 * One permission's usage. `used` is null where the provider reports no usage
 * at all, which is not the same claim as "not used", so that state is an
 * outline rather than a red dot.
 */
const UsageDot: FC<{ used: boolean | null }> = ({ used }) => (
  <Center
    boxSize={2.5}
    borderRadius="full"
    alignSelf="center"
    flexShrink={0}
    bg={used == null ? 'transparent' : used ? 'green.300' : 'red.400'}
    border={used == null ? '1px solid' : undefined}
    borderColor="gray.200"
  />
);

/**
 * What the dots mean. It sits beside the count rather than in the table
 * header, which carries column names only, as every other table here does.
 */
export const UsageLegend: FC<{ showUnknown?: boolean }> = ({ showUnknown }) => (
  <HStack spacing={3.5} color="gray.250" fontSize="14.4px">
    <HStack spacing={1.5}>
      <UsageDot used={true} />
      <Box>Used</Box>
    </HStack>
    <HStack spacing={1.5}>
      <UsageDot used={false} />
      <Box>Not Used</Box>
    </HStack>
    {showUnknown && (
      <HStack spacing={1.5}>
        <UsageDot used={null} />
        <Box>Unknown</Box>
      </HStack>
    )}
    <Box>Date = Last Use</Box>
  </HStack>
);

// The API groups by native name and class together. The class on its own is
// neither unique nor always present, so both are needed to identify a group.
const groupKey = (group: NativePermissions) =>
  `${group.native_name}::${group.native_class}`;

const formatLastUsed = (value: string | null) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const ResourceTypeCell: FC<{
  group: NativePermissions;
  supportsUsage: boolean;
}> = ({ group, supportsUsage }) => {
  const entries = permissionEntries(group);
  // Permissions with unknown usage are left out of both sides of this count.
  const known = entries.filter(([, used]) => used != null);
  const usedCount = known.filter(([, used]) => used).length;

  return (
    <WithResourceIcon resourceType={group.native_class || 'service'}>
      <StackedCell
        upper={group.native_name}
        lower={
          entries.length +
          ' permission' +
          (entries.length === 1
            ? ''
            : 's' +
              (supportsUsage && !!known.length ? ` · ${usedCount} used` : ''))
        }
      />
    </WithResourceIcon>
  );
};

const PermissionsCell: FC<{
  group: NativePermissions;
  expanded: boolean;
  onExpand(): void;
  supportsUsage: boolean;
  supportsPolicyType: boolean;
}> = ({ group, expanded, onExpand, supportsUsage, supportsPolicyType }) => {
  const entries = permissionEntries(group);
  const shown = expanded ? entries : entries.slice(0, ROWS_PER_TYPE);

  return (
    <Stack spacing={1.5} minW={0}>
      {shown.map(
        ([permission, used, grantedBy, lastUsedAt]: PermissionEntry) => {
          const lastUsed = formatLastUsed(lastUsedAt);
          const viaResourcePolicy = (grantedBy ?? []).includes(
            PermissionSource.ResourcePolicy,
          );
          return (
            <HStack key={permission} spacing={2.5} align="baseline">
              {supportsUsage && <UsageDot used={used} />}
              {/* The dot already says whether a permission was used, so the
                name does not dim to say it again. Nearly every permission is
                unused, which made the whole column look faded. */}
              <Text isTruncated>{permission}</Text>
              {supportsPolicyType && viaResourcePolicy && (
                <Box
                  px={2}
                  borderRadius="full"
                  border="1px solid"
                  borderColor="componentBorder"
                  bg="gray.50"
                  color="gray.250"
                  fontSize="sm"
                  whiteSpace="nowrap"
                  flexShrink={0}
                >
                  Resource Policy
                </Box>
              )}
              {!!lastUsed && (
                <Text
                  fontSize="xs"
                  color="gray.250"
                  whiteSpace="nowrap"
                  flexShrink={0}
                >
                  {lastUsed}
                </Text>
              )}
            </HStack>
          );
        },
      )}

      {entries.length > shown.length && (
        <Box
          as="button"
          alignSelf="flex-start"
          color="primary"
          fontSize="sm"
          onClick={onExpand}
        >
          {`Show all ${entries.length}`}
        </Box>
      )}
    </Stack>
  );
};

export const PermissionList: FC<PermissionListProps> = props => {
  const { level, search, isLoading, supportsUsage, supportsPolicyType } = props;
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // A group key is only unique within one response. Carrying an expansion
  // across responses would let a group the cap is there to hold back open
  // itself again for every later privilege and filter that shares its key.
  useEffect(() => setExpanded({}), [level]);

  // Searching replaces paging: with no pages left there was no other way to
  // reach one named permission in a bucket of thousands.
  const groups = useMemo(() => {
    const query = search.trim().toLowerCase();
    const all = level?.permissions_by_native ?? [];
    if (!query) return all;
    return all
      .map(group => ({
        ...group,
        permissions: permissionEntries(group).filter(([permission]) =>
          permission.toLowerCase().includes(query),
        ),
      }))
      .filter(group => group.permissions.length);
  }, [level, search]);

  const isSearching = !!search.trim();
  const columns = useMemo(
    () => [
      {
        header: 'Resource Type',
        accessor: 'native_name',

        render: ({ row }) => (
          <ResourceTypeCell group={row} supportsUsage={supportsUsage} />
        ),
      },
      {
        header: 'Permissions',
        accessor: 'permissions',
        align: 'left',
        styles: {
          cell: { w: '55%' },
        },
        render: ({ row }) => (
          <PermissionsCell
            group={row}
            expanded={isSearching || !!expanded[groupKey(row)]}
            onExpand={() =>
              setExpanded(current => ({
                ...current,
                [groupKey(row)]: true,
              }))
            }
            supportsUsage={supportsUsage}
            supportsPolicyType={supportsPolicyType}
          />
        ),
      },
    ],
    [expanded, isSearching, supportsPolicyType, supportsUsage],
  );

  return (
    <Box flex={1} w="full" h="full" minW={0} overflow="hidden">
      <Table
        w="full"
        columns={columns}
        data={groups as any[]}
        isLoading={isLoading}
        noDataMessage="No permissions match the selected filters"
        styles={{
          row: { verticalAlign: 'top' },
          cell: { verticalAlign: 'top' },
        }}
        testName="permissions"
      />
    </Box>
  );
};
